import { z } from 'zod';
import {
  paginatedResponse,
  paginationQuery,
  nonNegativeDecimalString as decimalString,
} from './common';

/**
 * Schemas for the dedicated `Edge` catalog table — Romanian "cant" / edge-banding,
 * one of the five pricing-engine entity tables.
 *
 * `pricePerLm` is per linear metre (RON canonical). Used by the Cabinet, Panel,
 * Countertop, Drawer calculators when computing edge cost (`2·(W+H)·pricePerLm`).
 *
 * `eligibleStructures` is a free-form array of structure tags (e.g.
 * "Egger H1180", "Kronospan 2266"). When non-empty, the UI/importer can suggest
 * only edges whose structures overlap the picked material's `structure`. Empty
 * array means "compatible with all materials".
 *
 * Source data: `excels/materiale.xlsx` sheet `Cant`.
 */

export const edgeCurrencyEnum = z.enum(['RON', 'EUR', 'USD']);
export type EdgeCurrencyValue = z.infer<typeof edgeCurrencyEnum>;

const optionalTrim = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

const optionalThicknessMm = z.number().int().nonnegative().max(50).optional().nullable();

export const edgeListItem = z.object({
  id: z.string(),
  name: z.string(),
  offerName: z.string().nullable(),
  sku: z.string().nullable(),
  manufacturer: z.string().nullable(),
  thicknessMm: z.number().int().nullable(),
  eligibleStructures: z.array(z.string()),
  pricePerLm: decimalString.optional(),
  currency: edgeCurrencyEnum,
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
});
export type EdgeListItem = z.infer<typeof edgeListItem>;

export const edgeDetails = edgeListItem.extend({
  attributes: z.unknown().nullable(),
});
export type EdgeDetails = z.infer<typeof edgeDetails>;

export const listEdgesQuery = paginationQuery.extend({
  search: optionalTrim(120),
  manufacturer: optionalTrim(120),
  structure: optionalTrim(120),
  active: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
    .optional(),
  includeDeleted: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
    .optional()
    .default(false),
});
export type ListEdgesQuery = z.infer<typeof listEdgesQuery>;

export const listEdgesResponse = paginatedResponse(edgeListItem);
export type ListEdgesResponse = z.infer<typeof listEdgesResponse>;

export const createEdgeRequest = z.object({
  name: z.string().trim().min(1).max(200),
  offerName: optionalTrim(200),
  sku: optionalTrim(80),
  manufacturer: optionalTrim(120),
  thicknessMm: optionalThicknessMm,
  eligibleStructures: z.array(z.string().trim().min(1).max(120)).max(50).default([]),
  pricePerLm: decimalString,
  currency: edgeCurrencyEnum.default('RON'),
  active: z.boolean().default(true),
  attributes: z.unknown().optional(),
});
export type CreateEdgeRequest = z.infer<typeof createEdgeRequest>;

export const updateEdgeRequest = createEdgeRequest.partial();
export type UpdateEdgeRequest = z.infer<typeof updateEdgeRequest>;
